import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * Header dropdown for switching between storefronts (e.g. English / 中文).
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The toggle button.
 * @property {HTMLDivElement} panel - The dropdown panel.
 *
 * @extends {Component<Refs>}
 */
class DropdownSiteSwitcher extends Component {
  requiredRefs = ['button', 'panel'];

  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Toggles the panel.
   */
  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  /**
   * Shows the panel.
   */
  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');
  }

  /**
   * Hides the panel.
   */
  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
  };

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleClickOutside);
    super.disconnectedCallback();
  }

  /**
   * @param {PointerEvent} event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.hidePanel();
    event.stopPropagation();
    this.refs.button?.focus();
  };
}

if (!customElements.get('dropdown-site-switcher')) {
  customElements.define('dropdown-site-switcher', DropdownSiteSwitcher);
}
